(() => {
  const CONFIGURATOR_SELECTOR = '[data-jjt-configurator]';

  function normaliseValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function formatMoney(cents, currencyCode = 'AUD') {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(cents % 100 === 0 ? 0 : 2)}`;
    }
  }

  function readVariants(configurator) {
    const variantScript = configurator.querySelector(
      '[data-jjt-product-variants]'
    );

    if (!variantScript) {
      return [];
    }

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      console.error(
        'JJ Timber Configurator: product variants could not be read.',
        error
      );

      return [];
    }
  }

  function getSelectedOptions(configurator) {
    const selectedOptions = {};

    configurator
      .querySelectorAll(
        'input[data-jjt-option-position]:checked, select[data-jjt-option-position]'
      )
      .forEach((input) => {
        const optionPosition = Number(
          input.dataset.jjtOptionPosition
        );

        if (!optionPosition) {
          return;
        }

        selectedOptions[optionPosition] = input.value;
      });

    return selectedOptions;
  }

  function variantMatchesOptions(variant, selectedOptions) {
    return Object.entries(selectedOptions).every(
      ([position, selectedValue]) => {
        const optionIndex = Number(position) - 1;
        const variantValue = variant.options[optionIndex];

        return (
          normaliseValue(variantValue) ===
          normaliseValue(selectedValue)
        );
      }
    );
  }

  function findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) =>
      variantMatchesOptions(variant, selectedOptions)
    );
  }

  function findCandidateVariant(
    variants,
    selectedOptions,
    optionPosition,
    optionValue
  ) {
    const candidateOptions = {
      ...selectedOptions,
      [optionPosition]: optionValue
    };

    return findMatchingVariant(variants, candidateOptions);
  }

  function updateSelectedHeadings(configurator) {
    configurator
      .querySelectorAll('[data-jjt-step]')
      .forEach((step) => {
        const checkedInput = step.querySelector(
          'input[type="radio"]:checked'
        );

        const selectedOutput = step.querySelector(
          '[data-jjt-selected-value]'
        );

        if (!checkedInput || !selectedOutput) {
          return;
        }

        selectedOutput.textContent = checkedInput.value;
      });
  }

  function updateSizePrices(
    configurator,
    variants,
    selectedOptions,
    currencyCode
  ) {
    configurator
      .querySelectorAll(
        '[data-jjt-option-price][data-jjt-price-type="absolute"]'
      )
      .forEach((priceOutput) => {
        const optionPosition = Number(
          priceOutput.dataset.jjtOptionPosition
        );

        const optionValue =
          priceOutput.dataset.jjtOptionValue;

        const candidateVariant = findCandidateVariant(
          variants,
          selectedOptions,
          optionPosition,
          optionValue
        );

        if (!candidateVariant) {
          priceOutput.textContent = '';
          return;
        }

        priceOutput.textContent = formatMoney(
          candidateVariant.price,
          currencyCode
        );
      });
  }

  function updateFinishPrices(
    configurator,
    variants,
    selectedOptions,
    currencyCode
  ) {
    const finishPriceOutputs = Array.from(
      configurator.querySelectorAll(
        '[data-jjt-option-price][data-jjt-price-type="difference"]'
      )
    );

    if (!finishPriceOutputs.length) {
      return;
    }

    const finishVariants = finishPriceOutputs
      .map((priceOutput) => {
        const optionPosition = Number(
          priceOutput.dataset.jjtOptionPosition
        );

        const optionValue =
          priceOutput.dataset.jjtOptionValue;

        const candidateVariant = findCandidateVariant(
          variants,
          selectedOptions,
          optionPosition,
          optionValue
        );

        return {
          priceOutput,
          candidateVariant
        };
      })
      .filter((item) => item.candidateVariant);

    finishPriceOutputs.forEach((priceOutput) => {
      priceOutput.textContent = '';
    });

    if (!finishVariants.length) {
      return;
    }

    const includedPrice = Math.min(
      ...finishVariants.map(
        (item) => item.candidateVariant.price
      )
    );

    finishVariants.forEach(
      ({ priceOutput, candidateVariant }) => {
        const priceDifference =
          candidateVariant.price - includedPrice;

        if (priceDifference <= 0) {
          priceOutput.textContent = 'Included';
          return;
        }

        priceOutput.textContent = `+${formatMoney(
          priceDifference,
          currencyCode
        )}`;
      }
    );
  }

  function updateCurrentTotals(
    configurator,
    currentVariant,
    currencyCode
  ) {
    if (!currentVariant) {
      return;
    }

    const formattedPrice = formatMoney(
      currentVariant.price,
      currencyCode
    );

    configurator
      .querySelectorAll('[data-jjt-current-total]')
      .forEach((totalOutput) => {
        totalOutput.textContent = formattedPrice;
      });

    const summaryTotal = configurator.querySelector(
      '[data-jjt-summary-total]'
    );

    if (summaryTotal) {
      summaryTotal.textContent = formattedPrice;
    }
  }

  function updateSummary(configurator) {
  const summaryChoices = configurator.querySelector(
    '[data-jjt-summary-choices]'
  );

  if (!summaryChoices) {
    return;
  }

  const labels = {
    1: 'Size',
    2: 'Finish',
    3: 'Umbrella Hole'
  };

  const checkedOptions = Array.from(
    configurator.querySelectorAll(
      'input[data-jjt-option-position]:checked'
    )
  ).sort((a, b) => {
    return (
      Number(a.dataset.jjtOptionPosition) -
      Number(b.dataset.jjtOptionPosition)
    );
  });

  summaryChoices.innerHTML = '';

  checkedOptions.forEach((input) => {
    const row = document.createElement('div');
    row.className = 'jjt-v2-summary__row';

    row.innerHTML = `
      <span class="jjt-v2-summary__label">
        ${labels[input.dataset.jjtOptionPosition] || input.dataset.jjtOptionName || 'Option'}:
      </span>

      <span class="jjt-v2-summary__value">
        ${input.value}
      </span>
    `;

    summaryChoices.appendChild(row);
  });
}

  function updateMainProductPrice(
    configurator,
    currentVariant,
    currencyCode
  ) {
    if (!currentVariant) {
      return;
    }

    const sectionId = configurator.dataset.section;

    if (!sectionId) {
      return;
    }

    const formattedPrice = formatMoney(
      currentVariant.price,
      currencyCode
    );

    const priceSelectors = [
      `#price-${sectionId} .price-item--regular`,
      `#price-${sectionId} .price-item--sale`,
      `#price-${sectionId} .price__regular .price-item`,
      `#price-${sectionId} .price__sale .price-item--sale`
    ];

    priceSelectors.forEach((selector) => {
      document
        .querySelectorAll(selector)
        .forEach((priceElement) => {
          priceElement.textContent = formattedPrice;
        });
    });
  }

  function updateVariantIdInput(
    configurator,
    currentVariant
  ) {
    if (!currentVariant) {
      return;
    }

    const sectionId = configurator.dataset.section;

    const productForm =
      document.getElementById(
        `product-form-${sectionId}`
      ) ||
      document.querySelector(
        `form[action*="/cart/add"]`
      );

    if (!productForm) {
      return;
    }

    const variantInput = productForm.querySelector(
      'input[name="id"]'
    );

    if (!variantInput) {
      return;
    }

    variantInput.value = currentVariant.id;
    variantInput.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function updateAddToCartButton(
    configurator,
    currentVariant
  ) {
    const sectionId = configurator.dataset.section;

    const addToCartButton =
      document.querySelector(
        `#ProductSubmitButton-${sectionId}`
      ) ||
      document.querySelector(
        `form[id="product-form-${sectionId}"] [name="add"]`
      );

    if (!addToCartButton || !currentVariant) {
      return;
    }

    const buttonText =
      addToCartButton.querySelector('span');

    if (currentVariant.available) {
      addToCartButton.disabled = false;
      addToCartButton.removeAttribute('aria-disabled');

      if (buttonText) {
        buttonText.textContent = 'Add to cart';
      }

      return;
    }

    addToCartButton.disabled = true;
    addToCartButton.setAttribute(
      'aria-disabled',
      'true'
    );

    if (buttonText) {
      buttonText.textContent = 'Sold out';
    }
  }

  function updateBrowserUrl(currentVariant) {
    if (!currentVariant || !window.history.replaceState) {
      return;
    }

    const currentUrl = new URL(window.location.href);

    currentUrl.searchParams.set(
      'variant',
      currentVariant.id
    );

    window.history.replaceState(
      {},
      '',
      currentUrl.toString()
    );
  }

  function refreshConfigurator(configurator) {
    if (!configurator) {
      return;
    }

    const variants = readVariants(configurator);

    if (!variants.length) {
      return;
    }

    const selectedOptions =
      getSelectedOptions(configurator);

    const currentVariant = findMatchingVariant(
      variants,
      selectedOptions
    );

    const currencyCode =
      configurator.dataset.currencyCode || 'AUD';

    updateSelectedHeadings(configurator);

    updateSizePrices(
      configurator,
      variants,
      selectedOptions,
      currencyCode
    );

    updateFinishPrices(
      configurator,
      variants,
      selectedOptions,
      currencyCode
    );

    updateCurrentTotals(
      configurator,
      currentVariant,
      currencyCode
    );

    updateSummary(configurator);

    updateMainProductPrice(
      configurator,
      currentVariant,
      currencyCode
    );

    updateVariantIdInput(
      configurator,
      currentVariant
    );

    updateAddToCartButton(
      configurator,
      currentVariant
    );

    updateBrowserUrl(currentVariant);
  }

  function initialiseConfigurator(configurator) {
    if (
      !configurator ||
      configurator.dataset.jjtInitialised === 'true'
    ) {
      return;
    }

    configurator.dataset.jjtInitialised = 'true';

    refreshConfigurator(configurator);

    configurator.addEventListener(
      'change',
      (event) => {
        const changedOption = event.target.closest(
          'input[data-jjt-option-position], select[data-jjt-option-position]'
        );

        if (!changedOption) {
          return;
        }

        const scrollPosition = window.scrollY;

        refreshConfigurator(configurator);

        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollPosition,
            left: 0,
            behavior: 'auto'
          });
        });
      }
    );
  }

  function initialiseAllConfigurators() {
    document
      .querySelectorAll(CONFIGURATOR_SELECTOR)
      .forEach((configurator) => {
        initialiseConfigurator(configurator);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAllConfigurators
    );
  } else {
    initialiseAllConfigurators();
  }

  document.addEventListener(
    'shopify:section:load',
    initialiseAllConfigurators
  );

  const mutationObserver = new MutationObserver(() => {
    initialiseAllConfigurators();
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();